import { FC, useContext, useEffect, useState } from "react";
import { AxiosError } from "axios";

import { AddIcon } from "../../assets/img/icons";
import Button from "../../components/Button";
import ConfirmModal from "../../components/ConfirmModal";
import ConnectedLayout from "../../components/ConnectedLayout";
import Header from "../../components/Header";
import TextInput from "../../components/TextInput";

import { toast } from "react-toastify";
import { AuthContext } from "../../contexts/AuthContext";
import {
    cancelInvite,
    getInvites,
    getInviteLink,
    inviteUser,
    resendInvite,
} from "../../services/api/invite";
import { GetInvites } from "../../types/api/invite";
import {
    FieldErrors,
    REQUIRED_FIELD,
    clearFieldError,
} from "../../utils/errors";
import { isValidEmail } from "../../utils/string";
import InvitesTable from "./InvitesTable";
import styles from "./styles.module.scss";

const Invite: FC = () => {
    const { hasPermission } = useContext(AuthContext)!;

    const [email, setEmail] = useState("");
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingInvites, setIsLoadingInvites] = useState(true);
    const [rowActionLoading, setRowActionLoading] = useState<
        Record<string, "resend" | "cancel" | "copyLink" | undefined>
    >({});
    const [cancelInviteEmail, setCancelInviteEmail] = useState<string | null>(
        null
    );

    const [invites, setInvites] = useState<GetInvites>([]);

    const fetchInvites = async () => {
        if (!hasPermission("invite", "read")) {
            setIsLoadingInvites(false);
            return;
        }

        try {
            const data = await getInvites();
            if (!data) {
                throw new Error("NO_DATA_RECEIVED");
            }
            setInvites(data);
        } catch (error) {
            if (error instanceof Error && error.message === "NO_DATA_RECEIVED") {
                toast.error("Aucune donnée reçue du serveur.");
            }
        } finally {
            setIsLoadingInvites(false);
        }
    };

    useEffect(() => {
        fetchInvites();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPermission]);

    const getInviteActionErrorMessage = (
        error: unknown,
        fallbackMessage: string
    ) => {
        if (error instanceof AxiosError) {
            const status = error.response?.status;
            const apiError = (error.response?.data as { error?: string } | undefined)
                ?.error;

            if (
                status === 404 ||
                apiError === "INVITE_NOT_FOUND_OR_ALREADY_USED"
            ) {
                return "Invitation introuvable ou déjà utilisée.";
            }
            if (status === 409 || apiError === "USER_ALREADY_REGISTERED") {
                return "Cet utilisateur est déjà inscrit.";
            }
        }

        return fallbackMessage;
    };

    const handleResendInvite = async (email: string) => {
        setRowActionLoading((prev) => ({ ...prev, [email]: "resend" }));
        try {
            await resendInvite(email);
            toast.success("Invitation renvoyée avec succès.");
            setIsLoadingInvites(true);
            await fetchInvites();
        } catch (error) {
            toast.error(
                getInviteActionErrorMessage(
                    error,
                    "Impossible de renvoyer l’invitation."
                )
            );
        } finally {
            setRowActionLoading((prev) => ({ ...prev, [email]: undefined }));
        }
    };

    const handleCancelInvite = (email: string) => {
        setCancelInviteEmail(email);
    };

    const handleCancelInviteConfirm = async () => {
        const email = cancelInviteEmail;
        if (!email) return;

        setRowActionLoading((prev) => ({ ...prev, [email]: "cancel" }));
        try {
            await cancelInvite(email);
            toast.success("Invitation annulée avec succès.");
            setIsLoadingInvites(true);
            await fetchInvites();
            setCancelInviteEmail(null);
        } catch (error) {
            toast.error(
                getInviteActionErrorMessage(
                    error,
                    "Impossible d’annuler l’invitation."
                )
            );
        } finally {
            setRowActionLoading((prev) => ({ ...prev, [email]: undefined }));
        }
    };

    const handleCopyInviteLink = async (email: string) => {
        setRowActionLoading((prev) => ({ ...prev, [email]: "copyLink" }));
        try {
            const data = await getInviteLink(email);

            await navigator.clipboard.writeText(data.url);
            toast.success("Lien copié");
        } catch (error) {
            if (error instanceof AxiosError) {
                const apiError = (error.response?.data as { error?: string })
                    ?.error;

                if (apiError === "INVITE_NOT_FOUND_OR_ALREADY_USED") {
                    toast.error("Invitation introuvable ou déjà utilisée");
                    return;
                }
            }

            toast.error("Impossible de copier le lien.");
        } finally {
            setRowActionLoading((prev) => ({ ...prev, [email]: undefined }));
        }
    };

    const handleInvite = async () => {
        if (!email.trim()) {
            setFieldErrors({ email: REQUIRED_FIELD });
            toast.error("Veuillez entrer une adresse e-mail.");
            return;
        }

        if (!isValidEmail(email)) {
            setFieldErrors({
                email: "Veuillez entrer une adresse e-mail valide.",
            });
            toast.error("Veuillez entrer une adresse e-mail valide.");
            return;
        }

        setFieldErrors({});
        setIsLoading(true);

        try {
            await inviteUser(email);
            setEmail("");
            toast.success("Invitation envoyée avec succès !");
        } catch (error) {
            setIsLoading(false);
            return;
        }

        try {
            setIsLoadingInvites(true);
            await fetchInvites();
        } catch (error) {
            toast.error("Erreur lors de la récupération des invitations.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <ConnectedLayout pageContentClassName={styles.connectedLayout}>
            <ConfirmModal
                open={Boolean(cancelInviteEmail)}
                handleClose={() => setCancelInviteEmail(null)}
                handleConfirm={handleCancelInviteConfirm}
                isLoading={
                    cancelInviteEmail
                        ? rowActionLoading[cancelInviteEmail] === "cancel"
                        : false
                }
                description={
                    cancelInviteEmail
                        ? `Confirmer l’annulation de l’invitation pour ${cancelInviteEmail} ?`
                        : undefined
                }
            />
            <Header icon={AddIcon} title="Invitations" />
            <div className={styles.container}>
                {hasPermission("invite", "create") && (
                    <div className={styles.form}>
                        <TextInput
                            className="resetpassword_input"
                            label="Adresse e-mail"
                            value={email}
                            setValue={(value) => {
                                setEmail(value);
                                setFieldErrors((prev) =>
                                    clearFieldError(prev, "email")
                                );
                            }}
                            error={fieldErrors.email}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    handleInvite();
                                }
                            }}
                        />
                        <Button
                            className={styles.submitButton}
                            label="Envoyer une invitation"
                            onClick={handleInvite}
                            isLoading={isLoading}
                        />
                    </div>
                )}
                {hasPermission("invite", "read") && (
                    <div className={styles.tableContainer}>
                        <InvitesTable
                            isLoading={isLoadingInvites}
                            invites={invites}
                            rowActionLoading={rowActionLoading}
                            onResendInvite={handleResendInvite}
                            onCancelInvite={handleCancelInvite}
                            onCopyInviteLink={handleCopyInviteLink}
                        />
                    </div>
                )}
            </div>
        </ConnectedLayout>
    );
};

export default Invite;
